import React, { Fragment } from 'react';
import { UiTable } from '@open-stack/ui';
import { useToggle } from '../../../../../hooks/toggle';
import { OrderProduct, OrderProductStatus } from '../../../../../XV2';
import { useTranslate } from '../../../../../services/translate';
import { useCurrency } from '../../../../../hooks/currency';
import sum from 'lodash/sum';
import groupBy from 'lodash/groupBy';
import styled from 'styled-components';
import { XText } from '../../../../x/text/XText';

const Wrapper = styled(UiTable)`
    overflow: initial;

    tr {
        &.${OrderProductStatus.CANCELLED} {
            text-decoration: line-through;
            color: var(--x-color-error);
        }
    }
`;

interface Props {
    orderProducts: OrderProduct[];
}

export function OrderViewCompactProductList({ orderProducts }: Props) {
    const { format } = useCurrency();
    const { t } = useTranslate();
    const [isProductDetailsVisible, toggleIsProductDetailsVisible] = useToggle();
    const amountTotal = sum(orderProducts.map((orderProduct) => orderProduct.priceCombined));

    const orderProductsByProductId = groupBy(orderProducts, (orderProduct) => orderProduct.productId);

    const orderProductsGrouped: (OrderProduct & { count: number; total: number })[] = Object.keys(
        orderProductsByProductId,
    ).map((id) => ({
        ...orderProductsByProductId[id][0],
        count: orderProductsByProductId[id].length,
        total: sum(
            orderProductsByProductId[id]
                .filter((item) => item.status !== OrderProductStatus.CANCELLED)
                .map(({ priceCombined }) => priceCombined),
        ),
    }));

    return (
        <>
            <Wrapper>
                <tbody
                    onClick={() => {
                        toggleIsProductDetailsVisible();
                    }}>
                    {isProductDetailsVisible
                        ? orderProducts.map((orderProduct) => (
                              <Fragment key={orderProduct.id}>
                                  <tr className={orderProduct.status}>
                                      <td>{orderProduct.productName}</td>
                                      <td align="right">{format(orderProduct.priceFinal)}</td>
                                  </tr>

                                  {orderProduct.orderProductModifiers.map((modifier) => (
                                      <tr className={orderProduct.status} key={modifier.id}>
                                          <td>
                                              <XText color="text-secondary">
                                                  • {modifier.modifierGroupName} -{' '}
                                                  {modifier.productName || modifier.modifierName}
                                              </XText>
                                          </td>

                                          <td align="right">
                                              {modifier.priceFinal > 0 && format(modifier.priceFinal)}
                                          </td>
                                      </tr>
                                  ))}
                              </Fragment>
                          ))
                        : orderProductsGrouped.map(({ id, productName, count, total }) => (
                              <Fragment key={id}>
                                  <tr onClick={toggleIsProductDetailsVisible}>
                                      <td>
                                          {count} x {productName}
                                      </td>
                                      <td align="right">{format(total)}</td>
                                  </tr>
                              </Fragment>
                          ))}

                    <tr>
                        <td align="right" colSpan={2}>
                            <b>
                                {t('order_page.total_cost')} <span>{format(amountTotal)}</span>
                            </b>
                        </td>
                    </tr>
                </tbody>
            </Wrapper>
        </>
    );
}
